interface EventData {
    event: string;
    eventName: string;
    [key: string]: unknown;
}

interface CreateGA4EventServiceOptions {
    pushEvent: (data: EventData, options?: { useNewRelic?: boolean }) => void;
}

export interface Item {
    item_id: string;
    item_name: string;
    affiliation?: string;
    coupon?: string;
    item_brand: string | undefined;
    item_category: string | undefined;
    item_category2: string | undefined;
    item_category3: string | undefined;
    item_variant?: string;
    currency?: string;
    price?: number;
    quantity?: number;
    price_known?: boolean;
}

interface ViewItem {
    item: Item;
}

interface ViewItemListOptions {
    items: Item[];
}

interface AddToCartOptions {
    items: Item[];
    currency: string;
    value: number;
}

interface RemoveFromCartOptions {
    items: Item[];
    currency: string;
    value: number;
}

interface BeginCheckoutOptions {
    items: Item[];
    currency: string;
    value: number;
}

interface AddPaymentInfoOptions {
    items: Item[];
    currency: string;
    value: number;
    coupon?: string;
    paymentType: 'PaymentCard' | 'None';
}

interface PurchaseOptions {
    items: Item[];
    currency: string;
    value: number;
    // computed from reservationIds to avoid duplicate purchase events in GA
    transactionId: string;
    coupon?: string;
    affiliation?: string;
    shipping?: number;
    tax?: number;
    reservationOwnerEmail?: string | null;
}

export const createGA4EventService = ({ pushEvent }: CreateGA4EventServiceOptions) => {
    const selectPromotion = (voucherCode: string) =>
        pushEvent({
            event: 'select_promotion',
            eventName: 'Select Promotion',
            promotion_id: voucherCode,
        });
    const viewItemList = ({ items }: ViewItemListOptions) =>
        pushEvent({
            event: 'view_item_list',
            eventName: 'View Item List',
            ecommerce: {
                items,
            },
            items,
        });
    const viewItem = ({ item }: ViewItem) =>
        pushEvent({
            event: 'view_item',
            eventName: 'View Item',
            ecommerce: {
                currency: item.currency,
                value: item.price,
                items: [item],
            },
            items: [item],
        });
    const addToCart = ({ items, currency, value }: AddToCartOptions) =>
        pushEvent({
            event: 'add_to_cart',
            eventName: 'Add To Cart',
            ecommerce: {
                currency,
                value,
                items,
            },
            items,
        });
    const removeFromCart = ({ items, currency, value }: RemoveFromCartOptions) =>
        pushEvent({
            event: 'remove_from_cart',
            eventName: 'Remove From Cart',
            ecommerce: {
                currency,
                value,
                items,
            },
            items,
        });
    const beginCheckout = ({ items, currency, value }: BeginCheckoutOptions) =>
        pushEvent({
            event: 'begin_checkout',
            eventName: 'Begin Checkout',
            ecommerce: {
                currency,
                value,
                items,
            },
            items,
        });
    const addPaymentInfo = ({ items, value, paymentType, coupon, currency, ...others }: AddPaymentInfoOptions) =>
        pushEvent({
            event: 'add_payment_info',
            eventName: 'Add Payment Info',
            ecommerce: {
                value,
                currency,
                coupon,
                payment_type: paymentType,
                items,
            },
            items,
            payment_type: paymentType,
            coupon,
            currency,
            value,
            ...others,
        });
    const purchase = ({
        items,
        affiliation,
        currency,
        transactionId,
        value,
        tax,
        coupon,
        shipping,
        reservationOwnerEmail,
        ...others
    }: PurchaseOptions) =>
        pushEvent(
            {
                event: 'purchase',
                eventName: 'Purchase',
                ecommerce: {
                    transaction_id: transactionId,
                    value,
                    tax,
                    shipping,
                    currency,
                    coupon,
                    items,
                    ...(reservationOwnerEmail != null ? { reservationOwnerEmail } : {}),
                },
                items,
                affiliation,
                transaction_id: transactionId,
                coupon,
                currency,
                value,
                shipping,
                tax,
                ...others,
            },
            { useNewRelic: true }
        );

    return {
        selectPromotion,
        viewItemList,
        viewItem,
        addToCart,
        removeFromCart,
        beginCheckout,
        addPaymentInfo,
        purchase,
    };
};
